import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNoIndex } from "../../hooks/usePageSeo";
import { HMSRoomProvider } from "@100mslive/react-sdk";
import {
  VideoCameraSlash,
  Clock,
  VideoCamera,
} from "@phosphor-icons/react";
import ContentLoader from "../../components/shared/ContentLoader";
import { getVideoMeetingGuestToken } from "../../firebase";
import VideoMeetingRoom from "../../components/videoMeeting/VideoMeetingRoom";
import "./MeetPage.css";

const ERROR_MESSAGES = {
  not_found: "הקישור אינו תקין או שהפגישה אינה קיימת.",
  inactive: "הפגישה בוטלה או שהקישור כבר אינו פעיל.",
  expired: "מועד הפגישה עבר והקישור אינו פעיל עוד.",
  monthly_limit:
    "לא ניתן להצטרף לשיחה כרגע. אנא פנו למטפל/ת לתיאום דרך חלופית.",
};

/**
 * Public patient join page (/meet/:linkId). No auth - the Cloud Function
 * validates the link, the appointment state, the clinic feature flag and the
 * monthly video quota before returning a guest token.
 */
function MeetPage() {
  const { linkId } = useParams();
  useNoIndex();

  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!linkId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getVideoMeetingGuestToken(linkId);
        if (!cancelled) setResult(data);
      } catch (error) {
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkId]);

  const formatStart = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const dateStr = d.toLocaleDateString("he-IL", {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: "Asia/Jerusalem",
    });
    const timeStr = d.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Jerusalem",
    });
    return `${dateStr} בשעה ${timeStr}`;
  };

  let body;
  if (isLoading) {
    body = (
      <div className="meet-page-state">
        <ContentLoader message="טוען את פגישת הווידאו..." />
      </div>
    );
  } else if (loadFailed) {
    body = (
      <div className="meet-page-state">
        <VideoCameraSlash size={32} weight="fill" />
        <span>שגיאה בטעינת הפגישה. נסו לרענן את העמוד.</span>
      </div>
    );
  } else if (result?.success) {
    body = (
      <HMSRoomProvider>
        <VideoMeetingRoom
          authToken={result.token}
          userName={result.patientName || "מטופל/ת"}
          title={
            result.clinicName
              ? `פגישת וידאו — ${result.clinicName}`
              : "פגישת וידאו"
          }
          subtitle={
            result.patientName ? `שלום ${result.patientName}!` : undefined
          }
        />
      </HMSRoomProvider>
    );
  } else if (result?.errorType === "not_open") {
    body = (
      <div className="meet-page-state">
        <Clock size={32} weight="fill" />
        <span>
          {result.patientName ? `שלום ${result.patientName}! ` : ""}
          הפגישה תיפתח כשעה לפני מועד תחילתה.
        </span>
        {result.startTime && (
          <span className="meet-page-start">
            מועד הפגישה: {formatStart(result.startTime)}
          </span>
        )}
        {result.clinicName && (
          <span className="meet-page-clinic">{result.clinicName}</span>
        )}
      </div>
    );
  } else {
    body = (
      <div className="meet-page-state">
        <VideoCameraSlash size={32} weight="fill" />
        <span>
          {ERROR_MESSAGES[result?.errorType] || ERROR_MESSAGES.not_found}
        </span>
      </div>
    );
  }

  return (
    <div className="meet-page" dir="rtl">
      <div className="meet-page-header">
        <VideoCamera size={18} weight="fill" />
        <span>פגישת וידאו</span>
      </div>
      <div className="meet-page-body">{body}</div>
    </div>
  );
}

export default MeetPage;
